<template>
  <div class="flex flex-col-reverse md:flex-row items-center gap-12 md:gap-32">
    <div class="md:w-1/2">
      <h2 class="text-3xl text-center md:text-left font-semibold mb-8">How It Works</h2>
      <Tabs :default-value="tabs[0]?.title" @update:model-value="handleTabChange">
        <TabsList class="w-full rounded-full h-12 max-w-sm border-grey-light-200 border bg-grey-light-50">
          <TabsTrigger
            v-for="tab in tabs"
            :key="tab.title"
            :value="tab.title"
            class="flex-1 rounded-full data-[state=active]:bg-primary text-grey-light-500 data-[state=active]:text-white cursor-pointer"
          >
            {{ tab.title }}
          </TabsTrigger>
        </TabsList>

        <TabsContent v-for="tab in tabs" :key="tab.title" :value="tab.title">
          <Accordion
            type="single"
            collapsible
            class="w-full"
            :default-value="`${tab.title}-0`"
            @update:model-value="(value) => handleAccordionChange(tab.title, value)"
          >
            <AccordionItem
              v-for="(accordion, index) in tab.accordians"
              :key="index"
              :value="`${tab.title}-${index}`"
              class="py-4"
            >
              <AccordionTrigger class="text-grey-light-900 hover:no-underline text-xl cursor-pointer">
                {{ accordion.title }}
                <template #icon>
                  <CirclePlus
                    :size="20"
                    class="icon-closed text-grey-light-400 pointer-events-none shrink-0 transition-opacity duration-200"
                  />
                  <CircleMinus
                    :size="20"
                    class="icon-open text-grey-light-400 pointer-events-none shrink-0 transition-opacity duration-200"
                  />
                </template>
              </AccordionTrigger>
              <AccordionContent class="text-grey-light-600 text-lg">
                {{ accordion.description }}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>
    </div>
    <div class="relative md:w-1/2 hidden md:block">
      <img :src="currentImageSrc" :alt="currentImageAlt" class="mx-auto max-h-[400px] md:max-h-[630px]" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { addBreadcrumb, createWarning } from '@/utils/monitoring.utils';
import { CircleMinus, CirclePlus } from 'lucide-vue-next';
import { computed, ref } from 'vue';

export interface HowItWorksAccordian {
  title: string;
  description: string;
  image?: string;
}

export interface HowItWorksTab {
  title: string;
  accordians: HowItWorksAccordian[];
  image: string;
}

interface Props {
  tabs: HowItWorksTab[];
}

const props = defineProps<Props>();

const currentTab = ref(props.tabs[0]?.title || '');
const currentAccordionValue = ref<Record<string, string | undefined>>({});

if (props.tabs[0]) {
  currentAccordionValue.value[props.tabs[0].title] = `${props.tabs[0].title}-0`;
}

const handleTabChange = (tabTitle: string | number) => {
  const tabTitleStr = String(tabTitle);
  currentTab.value = tabTitleStr;
  if (!currentAccordionValue.value[tabTitleStr]) {
    currentAccordionValue.value[tabTitleStr] = `${tabTitleStr}-0`;
  }
};

const handleAccordionChange = (tabTitle: string, value: string | string[] | undefined) => {
  const stringValue = Array.isArray(value) ? value[0] : value;
  currentAccordionValue.value[tabTitle] = stringValue;
};

const currentImage = computed(() => {
  const tab = props.tabs.find((t) => t.title === currentTab.value);
  if (!tab) return '';

  const accordionValue = currentAccordionValue.value[currentTab.value];
  if (accordionValue) {
    const parts = accordionValue.split('-');
    const lastPart = parts[parts.length - 1];
    if (lastPart) {
      const index = parseInt(lastPart);

      if (!isNaN(index) && index >= 0 && index < tab.accordians.length) {
        const accordion = tab.accordians[index];
        if (accordion?.image) {
          return accordion.image;
        }
      }
    }
  }

  return tab.image;
});

const currentImageSrc = computed(() => {
  const imagePath = currentImage.value;
  if (!imagePath) return '';

  try {
    return new URL(`../assets/images/how-it-works/${imagePath}`, import.meta.url).href;
  } catch (e) {
    addBreadcrumb('image.load', `Failed to load image ${imagePath}`);
    createWarning(e);
    return '';
  }
});

const currentImageAlt = computed(() => {
  const tab = props.tabs.find((t) => t.title === currentTab.value);
  if (!tab) return '';

  const accordionValue = currentAccordionValue.value[currentTab.value];
  if (accordionValue) {
    const parts = accordionValue.split('-');
    const lastPart = parts[parts.length - 1];
    if (lastPart) {
      const index = parseInt(lastPart);

      if (!isNaN(index) && index >= 0 && index < tab.accordians.length) {
        const accordion = tab.accordians[index];
        return accordion?.title || tab.title;
      }
    }
  }

  return tab.title;
});
</script>

<style scoped>
[data-state='open'] .icon-closed {
  display: none;
}

[data-state='closed'] .icon-open {
  display: none;
}
</style>
